import { useState } from 'react';
import {
  Avatar, Card, Flex, Grid, GridItem,
  IconButton, Menu, Portal, Text,
} from '@chakra-ui/react';
import { formatDate } from '@utils/date';
import { InfoCol } from '@components/InfoCol';
import { LuEllipsisVertical } from 'react-icons/lu';
import type { UserType } from '@customTypes/user';
import { useNavigate } from 'react-router';
import { useAuthContext } from '@contexts/auth';

import ConfirmDisableDialog from './ConfirmDisableDialog';

type Props = {
  user: UserType,
  refetch: () => void,
};

const UserCard = ({ user, refetch }: Props) => {
  const navigate = useNavigate();

  const [showConfirmDisableDialog, setShowConfirmDisableDialog] = useState(false);

  const { checkIfHasRole } = useAuthContext();

  const canBlockUsers = checkIfHasRole('CAN_BLOCK_USERS');
  const canManageUsers = checkIfHasRole('CAN_MANAGE_USERS');

  const showMenu = canBlockUsers || canManageUsers;

  return (
    <Card.Root
      size="sm"
      variant="elevated"
      bg="grayscale.fg"
      mt={4}
      px={4}
      py={3}
    >
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Grid templateColumns="repeat(6, 1fr)" gap="2" flex="1">
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <Flex gap={1} mb={{ base: 2, lg: 0 }} flexDirection="column">
              <Text fontSize="sm" color="text.muted" fontWeight={600}>Nome</Text>
              <Flex alignItems="center" gap={2}>
                <Avatar.Root variant="outline" borderColor="border.muted" size="xs">
                  <Avatar.Fallback name={user.name} />
                  {user.thumbnail_url && (
                    <Avatar.Image src={user.thumbnail_url} />
                  )}
                </Avatar.Root>
                <Text fontSize="md">{user.name || 'Não informado'}</Text>
              </Flex>
            </Flex>
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <InfoCol
              label="E-mail"
              value={user.email}
            />
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <InfoCol
              label="Bloqueado até"
              value={formatDate(user.lockout_end) || 'Não bloqueado'}
            />
          </GridItem>
        </Grid>

        {showMenu && (
          <Flex alignItems="center">
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton variant="ghost" size="sm">
                  <LuEllipsisVertical />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    {canManageUsers && (
                      <Menu.Item
                        value="editRoles"
                        onClick={() => navigate(`/painel/usuarios/${user.id}/permissoes`, { state: { userName: user.name } })}
                      >
                        Alterar permissões
                      </Menu.Item>
                    )}
                    {canBlockUsers && (
                      <Menu.Item
                        value="delete"
                        color="fg.error"
                        _hover={{ bg: "bg.error", color: "fg.error" }}
                        onClick={() => setShowConfirmDisableDialog(true)}
                      >
                        Desabilitar
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>     
          </Flex>
        )}
      </Flex>

      {showConfirmDisableDialog && (
        <ConfirmDisableDialog
          user={user}
          handleClose={() => setShowConfirmDisableDialog(false)}
          afterSubmit={() => {
            refetch();
            setShowConfirmDisableDialog(false);
          }}
        />
      )}
    </Card.Root>
  );
};

export default UserCard;
